import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getPublicVenue,
  getPublicVenueBySubdomain,
} from "./publicVenueApi";
import PublicBookingModal from "./PublicBookingModal";
import PublicPaymentResult from "./PublicPaymentResult";
import {
  ONGROUND_LOGO_SURFACE,
  resolveOngroundLogoForSurface,
} from "./ongroundBranding";
import {
  normalizeWebsiteExtensions,
  resolveWebsiteCopy,
  resolveWebsiteMedia,
} from "./publicWebsiteContent";
import {
  DEFAULT_WEBSITE_SECTION_ORDER,
  WEBSITE_SECTION_KEYS,
  WEBSITE_THEME_LAYOUTS,
} from "./themeDefinitions";

import {
  findThemeComponent,
  getThemeComponent,
} from "./themeComponents";
import "./publicVenue.css";
import "./publicPaymentTicket.css";
import "./themes/classicTheme.css";
import "./themes/sportsTheme.css";
import "./themes/modernTheme.css";
import "./themes/minimalTheme.css";
import "./themes/darkTheme.css";
import "./themes/arenaTheme.css";



const DEFAULT_SECTION_ORDER = DEFAULT_WEBSITE_SECTION_ORDER;

const ARENA_COPY_KEYS = [
  "hoursPrefix",
  "hoursSuffix",
  "heroBadge",
  "groundsEyebrow",
  "groundsTitle",
  "groundsDescription",
  "groundCapacitySuffix",
  "pricePrefix",
  "priceOnRequestLabel",
  "groundBookLabel",
  "emptyGroundsLabel",
  "bookingEyebrow",
  "bookingTitle",
  "bookingDescription",
  "bookingStepOneTitle",
  "bookingStepOneDescription",
  "bookingStepTwoTitle",
  "bookingStepTwoDescription",
  "bookingStepThreeTitle",
  "bookingStepThreeDescription",
  "bookingButtonLabel",
  "testimonialsEyebrow",
  "testimonialsTitle",
  "aboutEyebrow",
  "aboutTitle",
  "locationEyebrow",
  "locationContactLabel",
  "contactEyebrow",
  "contactTitle",
  "contactDescription",
  "contactOpenLabel",
  "contactOpenDescription",
  "faqEyebrow",
  "faqTitle",
  "footerQuickLinksTitle",
  "footerBookLabel",
  "footerAboutLabel",
  "footerContactLabel",
  "footerStartBookingLabel",
  "footerVisitTitle",
  "footerCopyrightSuffix",
  "footerPoweredByText",
];

const createDefaultArenaConfig = () => ({
  contactEmail: "",
  rating: 0,
  reviewCount: 0,

  copy: ARENA_COPY_KEYS.reduce((result, key) => {
    result[key] = "";
    return result;
  }, {}),

  testimonials: [],
  faqs: [],
  stats: [],

  social: {
    instagramUrl: "",
    youtubeUrl: "",
    facebookUrl: "",
  },

  app: {
    enabled: false,
    badge: "",
    title: "",
    highlightedTitle: "",
    description: "",
    features: [],
    googlePlayUrl: "",
    appStoreUrl: "",
    proofText: "",
    ctaLabel: "",
    greeting: "",
    customerName: "",
    featuredLabel: "",
    featuredTime: "",
    slotsLabel: "",
    confirmLabel: "",
    ratingText: "",
    reviewsText: "",
    instantTitle: "",
    instantSubtitle: "",
  },
});

const normalizeArenaConfig = (value) => {
  const defaults = createDefaultArenaConfig();

  const input =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : {};

  const copy =
    input.copy &&
    typeof input.copy === "object" &&
    !Array.isArray(input.copy)
      ? input.copy
      : {};

  const social =
    input.social &&
    typeof input.social === "object" &&
    !Array.isArray(input.social)
      ? input.social
      : {};

  const app =
    input.app &&
    typeof input.app === "object" &&
    !Array.isArray(input.app)
      ? input.app
      : {};

  return {
    ...defaults,
    ...input,

    copy: {
      ...defaults.copy,
      ...copy,
    },

    testimonials: Array.isArray(input.testimonials)
      ? input.testimonials
      : [],

    faqs: Array.isArray(input.faqs)
      ? input.faqs
      : [],

    stats: Array.isArray(input.stats)
      ? input.stats
      : [],

    social: {
      ...defaults.social,
      ...social,
    },

    app: {
      ...defaults.app,
      ...app,
      features: Array.isArray(app.features)
        ? app.features
        : [],
    },
  };
};

const DEFAULT_PUBLIC_WEBSITE = {
  enabled: true,
  status: "published",
  theme: {
    layout: "classic",
    primaryColor: "#33A444",
    accentColor: "#111827",
    backgroundStyle: "clean",
  },
  hero: {
    headline: "",
    subheadline: "",
    ctaLabel: "Book Now",
  },

  copy: {},
  media: {},
  themeContent: {},

  arena: createDefaultArenaConfig(),

  sections: WEBSITE_SECTION_KEYS.reduce((sections, key) => {
    sections[key] = true;
    return sections;
  }, {}),
  sectionOrder: DEFAULT_SECTION_ORDER,
  booking: {
    enabled: true,
    slotSize: 60,
    requirePhone: true,
    requireEmail: false,
  },
  seo: {
    title: "",
    description: "",
    shareImageKey: "",
  },
};

const normalizeWebsite = (value) => {
  const input = value && typeof value === "object" ? value : {};

  const extensions = normalizeWebsiteExtensions(input);

  return {
    ...DEFAULT_PUBLIC_WEBSITE,
    ...input,
    ...extensions,
    theme: {
      ...DEFAULT_PUBLIC_WEBSITE.theme,
      ...(input.theme || {}),
      layout: WEBSITE_THEME_LAYOUTS.includes(input.theme?.layout)
        ? input.theme.layout
        : DEFAULT_PUBLIC_WEBSITE.theme.layout,
    },
    hero: {
      ...DEFAULT_PUBLIC_WEBSITE.hero,
      ...(input.hero || {}),
    },

    arena: normalizeArenaConfig(input.arena),

    sections: {
      ...DEFAULT_PUBLIC_WEBSITE.sections,
      ...(input.sections || {}),
    },
    sectionOrder:
      Array.isArray(input.sectionOrder) && input.sectionOrder.length
        ? input.sectionOrder.filter((key) =>
            DEFAULT_SECTION_ORDER.includes(key),
          )
        : DEFAULT_SECTION_ORDER,
    booking: {
      ...DEFAULT_PUBLIC_WEBSITE.booking,
      ...(input.booking || {}),
    },
    seo: {
      ...DEFAULT_PUBLIC_WEBSITE.seo,
      ...(input.seo || {}),
    },
  };
};

const formatText = (value) => {
  if (!value) return "—";
  return String(value).replace(/_/g, " ");
};

const getGroundPrice = (ground, venue, priceOnRequestLabel) => {
  const amount =
    ground?.basePrice ??
    ground?.price ??
    ground?.pricing?.basePrice ??
    venue?.pricing?.basePrice;

  const currency =
    ground?.currency ||
    ground?.pricing?.currency ||
    venue?.pricing?.currency ||
    "INR";

  if (amount === undefined || amount === null || amount === "")
    return priceOnRequestLabel;

  return `${currency} ${amount}`;
};

const PublicVenuePage = ({
  slugOverride = "",
  subdomainOverride = "",
}) => {
  const { slug: routeSlug } = useParams();
  const isSubdomainRoute = Boolean(subdomainOverride);
  const [resolvedSlug, setResolvedSlug] = useState(
    isSubdomainRoute ? "" : routeSlug || slugOverride || "",
  );
  const slug = isSubdomainRoute
    ? resolvedSlug
    : resolvedSlug || routeSlug || slugOverride;
  const location = useLocation();
  const navigate = useNavigate();

  const [venue, setVenue] = useState(null);
  const [grounds, setGrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingGround, setBookingGround] = useState(null);
  const [bookDeepLinkHandled, setBookDeepLinkHandled] = useState(false);
  const [paymentRecoveryGateway, setPaymentRecoveryGateway] =
    useState("");
  const [paymentRecoveryIntent, setPaymentRecoveryIntent] =
    useState("");

  useEffect(() => {
    let active = true;

    const loadVenue = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const data = isSubdomainRoute
          ? await getPublicVenueBySubdomain(subdomainOverride)
          : await getPublicVenue(routeSlug || slugOverride);

        if (!active) return;

        setVenue(data.venue);
        setGrounds(data.grounds);
        setResolvedSlug(
          data?.venue?.slug || routeSlug || slugOverride || "",
        );
      } catch (error) {
        if (!active) return;

        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load venue website.",
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    if (isSubdomainRoute || routeSlug || slugOverride) {
      loadVenue();
    }

    return () => {
      active = false;
    };
  }, [isSubdomainRoute, routeSlug, slugOverride, subdomainOverride]);

  const website = useMemo(
    () => normalizeWebsite(venue?.publicWebsite),
    [venue?.publicWebsite],
  );

  const t = useCallback(
    (key, fallback) =>
      resolveWebsiteCopy(website, key, fallback),
    [website],
  );

  const paymentResult = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const validResults = [
      "success",
      "pending",
      "failed",
      "error",
    ];
    const razorpayResult = params
      .get("razorpay_payment")
      ?.trim()
      .toLowerCase();
    const esewaResult = params
      .get("esewa_payment")
      ?.trim()
      .toLowerCase();

    if (validResults.includes(razorpayResult)) {
      return {
        type: razorpayResult,
        gateway: "RAZORPAY",
      };
    }

    if (validResults.includes(esewaResult)) {
      return {
        type: esewaResult,
        gateway: "ESEWA",
      };
    }

    return null;
  }, [location.search]);

  const paymentResultType = paymentResult?.type || null;
  const paymentResultGateway =
    paymentResult?.gateway || "";

  const themeLayout = website.theme?.layout || "classic";

  const primaryColor = website.theme?.primaryColor || "#33A444";
  const accentColor = website.theme?.accentColor || "#111827";
  const backgroundStyle = website.theme?.backgroundStyle || "clean";

  const gallery = Array.isArray(venue?.media?.gallery)
    ? venue.media.gallery
    : [];
  const heroImage = resolveWebsiteMedia(
    website,
    "heroImageUrl",
    venue?.media?.bannerImage || gallery[0] || "",
  );

  const headline =
    website.hero?.headline ||
    venue?.name ||
    t(
      "heroHeadlineFallback",
      "Book your favourite sports venue",
    );

  const subheadline =
    website.hero?.subheadline ||
    venue?.shortDescription ||
    t(
      "heroSubheadlineFallback",
      "Explore facilities, available grounds, timings, pricing, and book your slot online.",
    );

  const ctaLabel =
    website.hero?.ctaLabel ||
    t("heroCtaFallback", "Book Now");

  const isWebsiteVisible =
    website.enabled !== false && website.status !== "unpublished";

  const openBooking = useCallback(
    (ground = null) => {
      setBookingGround(ground || grounds[0] || null);
      setBookingOpen(true);
    },
    [grounds],
  );

  useEffect(() => {
    if (
      bookDeepLinkHandled ||
      !isSubdomainRoute ||
      location.pathname !== "/book" ||
      loading ||
      !venue ||
      !slug ||
      grounds.length === 0
    ) {
      return;
    }

    setBookDeepLinkHandled(true);
    openBooking();
  }, [
    bookDeepLinkHandled,
    grounds.length,
    isSubdomainRoute,
    loading,
    location.pathname,
    openBooking,
    slug,
    venue,
  ]);

  const retryPayment = useCallback(() => {
    // Both gateways reopen the saved booking. eSewa then runs its
    // abandon/reconcile policy; Razorpay restores the same order and either
    // reopens Checkout or re-checks the signed successful payment payload.
    setPaymentRecoveryIntent("retry");
    setPaymentRecoveryGateway(
      paymentResultGateway || "ESEWA",
    );

    if (isSubdomainRoute) {
      navigate(
        { pathname: "/", search: "", hash: "" },
        { replace: true },
      );
    } else {
      navigate(`/venues/${encodeURIComponent(slug || "")}`, {
        replace: true,
      });
    }

    openBooking();
  }, [
    navigate,
    openBooking,
    isSubdomainRoute,
    paymentResultGateway,
    slug,
  ]);

  const chooseDifferentTime = useCallback(() => {
    // Do not clear the saved payment attempt here. The booking modal must
    // first run the existing server-side abandon/reconcile flow so a hold or
    // in-flight payment cannot be silently orphaned.
    setPaymentRecoveryIntent("choose_time");
    setPaymentRecoveryGateway(
      paymentResultGateway || "ESEWA",
    );

    if (isSubdomainRoute) {
      navigate(
        { pathname: "/", search: "", hash: "" },
        { replace: true },
      );
    } else {
      navigate(`/venues/${encodeURIComponent(slug || "")}`, {
        replace: true,
      });
    }

    openBooking();
  }, [
    navigate,
    openBooking,
    isSubdomainRoute,
    paymentResultGateway,
    slug,
  ]);

  const handlePaymentRecoveryHandled = useCallback(() => {
    setPaymentRecoveryGateway("");
    setPaymentRecoveryIntent("");
  }, []);

  useEffect(() => {
    if (!venue) return;

    const seoTitle =
      website.seo?.title ||
      venue.name ||
      t("seoTitleFallback", "Venue Booking");

    const seoDescription =
      website.seo?.description ||
      venue.shortDescription ||
      t(
        "seoDescriptionFallback",
        "Book sports venues online.",
      );

    document.title = seoTitle;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", seoDescription);
  }, [venue, website, t]);

  const renderAbout = () => (
    <section className="pv-section" id="about">
      <div className="pv-section-head">
        <span>{t("aboutEyebrow", "About")}</span>
        <h2>{t("aboutTitle", "About this venue")}</h2>
      </div>

      <div className="pv-card pv-text-card">
        {venue?.shortDescription && <p>{venue.shortDescription}</p>}
        <p>
          {venue?.fullDescription ||
            t(
              "aboutEmptyText",
              "Venue details will be available soon.",
            )}
        </p>
      </div>
    </section>
  );

  const renderGallery = () => (
    <section className="pv-section" id="gallery">
      <div className="pv-section-head">
        <span>{t("galleryEyebrow", "Gallery")}</span>
        <h2>{t("galleryTitle", "Explore the venue")}</h2>
      </div>

      {gallery.length ? (
        <div className="pv-gallery">
          {gallery.slice(0, 6).map((image, index) => (
            <img
              key={`${image}-${index}`}
              src={image}
              alt={`${venue?.name || "Venue"} gallery ${index + 1}`}
              loading="lazy"
            />
          ))}
        </div>
      ) : (
        <div className="pv-empty">
          {t(
            "galleryEmptyText",
            "Gallery images are not available yet.",
          )}
        </div>
      )}
    </section>
  );

  const renderFacilities = () => (
    <section className="pv-section" id="facilities">
      <div className="pv-section-head">
        <span>{t("facilitiesEyebrow", "Facilities")}</span>
        <h2>{t("facilitiesTitle", "What customers get")}</h2>
      </div>

      {venue?.facilities?.length ? (
        <div className="pv-chip-grid">
          {venue.facilities.map((facility) => (
            <span className="pv-chip" key={facility}>
              {formatText(facility)}
            </span>
          ))}
        </div>
      ) : (
        <div className="pv-empty">
          {t(
            "facilitiesEmptyText",
            "Facilities are not added yet.",
          )}
        </div>
      )}
    </section>
  );

  const renderGrounds = () => (
    <section className="pv-section" id="grounds">
      <div className="pv-section-head">
        <span>{t("groundsEyebrow", "Grounds")}</span>
        <h2>{t("groundsTitle", "Choose your play area")}</h2>
      </div>

      {grounds.length ? (
        <div className="pv-ground-grid">
          {grounds.map((ground) => (
            <article className="pv-ground-card" key={ground._id}>
              <div>
                <h3>
                  {ground.name ||
                    formatText(ground.type) ||
                    t("groundFallbackName", "Ground")}
                </h3>
                <p>{formatText(ground.type)}</p>
              </div>

              <div className="pv-ground-meta">
                {ground.capacity ? (
                  <span>
                    {t("groundCapacityLabel", "Capacity")}: {ground.capacity}
                  </span>
                ) : null}
                <span>
                  {getGroundPrice(
                    ground,
                    venue,
                    t("priceOnRequestLabel", "Price on request"),
                  )}
                </span>
              </div>

              {website.booking?.enabled !== false && (
                <button type="button" onClick={() => openBooking(ground)}>
                  {ctaLabel}
                </button>
              )}
            </article>
          ))}
        </div>
      ) : (
        <div className="pv-empty">
          {t(
            "groundsEmptyText",
            "No active grounds are available yet.",
          )}
        </div>
      )}
    </section>
  );

  const renderPricing = () => (
    <section className="pv-section" id="pricing">
      <div className="pv-section-head">
        <span>{t("pricingEyebrow", "Pricing")}</span>
        <h2>{t("pricingTitle", "Simple pricing overview")}</h2>
      </div>

      <div className="pv-card pv-pricing-card">
        <div>
          <span>{t("pricingStartingLabel", "Starting from")}</span>
          <strong>
            {getGroundPrice(
              {},
              venue,
              t("priceOnRequestLabel", "Price on request"),
            )}
          </strong>
        </div>
        <p>
          {t(
            "pricingDescription",
            "Final slot pricing is calculated during booking based on selected ground and time.",
          )}
        </p>
      </div>
    </section>
  );

  const renderBooking = () => (
    <section className="pv-section" id="public-booking">
      <div className="pv-section-head">
        <span>{t("bookingEyebrow", "Booking")}</span>
        <h2>{t("bookingTitle", "Book your slot online")}</h2>
      </div>

      <div className="pv-booking-card">
        <div>
          <h3>{t("bookingCardTitle", "Ready to play?")}</h3>
          <p>
            {t(
              "bookingCardDescription",
              "Select a ground, date, and available time slot. We will temporarily hold the slot while payment is completed.",
            )}
          </p>
        </div>

        <button type="button" onClick={() => openBooking()}>
          {t("bookingSelectLabel", "Select Slot")}
        </button>
      </div>
    </section>
  );

  const renderLocation = () => (
    <section className="pv-section" id="location">
      <div className="pv-section-head">
        <span>{t("locationEyebrow", "Location")}</span>
        <h2>{t("locationTitle", "Find the venue")}</h2>
      </div>

      <div className="pv-card pv-location-card">
        <div>
          <strong>
            {venue?.location?.address ||
              t("addressUnavailableText", "Address not available")}
          </strong>
          <span>
            {[venue?.location?.city, venue?.location?.country]
              .filter(Boolean)
              .join(", ") ||
              t(
                "locationUnavailableText",
                "Location details not available",
              )}
          </span>
        </div>

        {venue?.googleMapsUrl && (
          <a href={venue.googleMapsUrl} target="_blank" rel="noreferrer">
            {t("openMapsLabel", "Open in Maps")}
          </a>
        )}
      </div>
    </section>
  );

  const renderPolicies = () => (
    <section className="pv-section" id="policies">
      <div className="pv-section-head">
        <span>{t("policiesEyebrow", "Policies")}</span>
        <h2>{t("policiesTitle", "Rules and cancellation")}</h2>
      </div>

      <div className="pv-policy-grid">
        <div className="pv-card">
          <h3>{t("cancellationTitle", "Cancellation")}</h3>
          <p>
            {venue?.policies?.cancellationPolicy ||
              t("cancellationEmptyText", "Policy not added yet.")}
          </p>
        </div>

        <div className="pv-card">
          <h3>{t("rulesTitle", "Rules & Terms")}</h3>
          <p>
            {venue?.policies?.rulesTerms ||
              t("rulesEmptyText", "Rules are not added yet.")}
          </p>
        </div>
      </div>
    </section>
  );

  const renderContact = () => (
    <section className="pv-section" id="contact">
      <div className="pv-section-head">
        <span>{t("contactEyebrow", "Contact")}</span>
        <h2>{t("contactTitle", "Need help?")}</h2>
      </div>

      <div className="pv-card pv-contact-card">
        <div>
          <strong>
            {venue?.contact?.label ||
              t("contactFallbackLabel", "Venue contact")}
          </strong>
          <span>
            {venue?.contact?.e164 ||
              t(
                "contactUnavailableText",
                "Contact number not available",
              )}
          </span>
        </div>
      </div>
    </section>
  );

  const sectionRenderers = {
    about: renderAbout,
    gallery: renderGallery,
    facilities: renderFacilities,
    grounds: renderGrounds,
    pricing: renderPricing,
    booking: renderBooking,
    location: renderLocation,
    policies: renderPolicies,
    contact: renderContact,
  };

  if (loading) {
    return (
      <main className="pv-venue-loading-shell">
        <section
          className="pv-venue-loader"
          role="status"
          aria-live="polite"
        >
          <div className="pv-venue-loader__mark">
            <img
              src={resolveOngroundLogoForSurface(
                ONGROUND_LOGO_SURFACE.LIGHT,
              )}
              alt="Onground"
            />
          </div>

          <div className="pv-venue-loader__status">
            Loading venue{"\u2026"}
          </div>
        </section>
      </main>
    );
  }

  if (errorMessage || !venue) {
    return (
      <main className="pv-page">
        <div className="pv-error-card">
          <h1>Venue not available</h1>
          <p>{errorMessage || "This venue website could not be loaded."}</p>
        </div>
      </main>
    );
  }

  if (paymentResultType) {
    return (
      <PublicPaymentResult
        resultType={paymentResultType}
        venue={venue}
        website={website}
        slug={slug}
        search={location.search}
        venueHrefOverride={
          isSubdomainRoute
            ? "/"
            : `/venues/${encodeURIComponent(slug || "")}`
        }
        primaryColor={primaryColor}
        accentColor={accentColor}
        onTryAgain={retryPayment}
        onChooseDifferentTime={chooseDifferentTime}
        paymentGateway={paymentResultGateway || "ESEWA"}
      />
    );
  }

  if (!isWebsiteVisible) {
    return (
      <main className="pv-page">
        <div className="pv-error-card">
          <h1>
            {t("websiteUnpublishedTitle", "Website not published")}
          </h1>
          <p>
            {t(
              "websiteUnpublishedDescription",
              "This venue website is currently not available for public visitors.",
            )}
          </p>
        </div>
      </main>
    );
  }

  const SelectedTheme =
    getThemeComponent(themeLayout);

  const renderedThemeLayout =
    findThemeComponent(themeLayout)
      ? themeLayout
      : "classic";

  return (
    <>
      <SelectedTheme
        venue={venue}
        grounds={grounds}
        website={website}
        backgroundStyle={backgroundStyle}
        primaryColor={primaryColor}
        accentColor={accentColor}
        heroImage={heroImage}
        headline={headline}
        subheadline={subheadline}
        ctaLabel={ctaLabel}
        openBooking={openBooking}
        sectionRenderers={sectionRenderers}
        themeLayout={renderedThemeLayout}
        selectedThemeLayout={themeLayout}
      />

      <PublicBookingModal
        open={bookingOpen}
        slug={slug}
        venue={venue}
        grounds={grounds}
        initialGround={bookingGround}
        website={website}
        primaryColor={primaryColor}
        accentColor={accentColor}
        resumeEsewaRecovery={paymentRecoveryGateway === "ESEWA"}
        onEsewaRecoveryHandled={handlePaymentRecoveryHandled}
        resumeRazorpayRecovery={paymentRecoveryGateway === "RAZORPAY"}
        onRazorpayRecoveryHandled={handlePaymentRecoveryHandled}
        recoveryIntent={paymentRecoveryIntent}
        onClose={() => setBookingOpen(false)}
        onHoldCreated={() => {}}
      />
    </>
  );
};

export default PublicVenuePage;
